import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DireccionEspanaFields from "@/components/DireccionEspanaFields";

const VACIO = {
  nombre: "", codigo: "", nif: "", email: "", telefono: "",
  direccion: "", poblacion: "", provincia: "", codigo_postal: "", notas: "",
  geocodificar: true, es_potencial: false,
};

export default function ProyectosClientes({ onChange }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const cargar = useCallback(async () => {
    try { const { data } = await api.get("/proyectos/clientes"); setItems(data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  const guardar = async () => {
    if (!form.nombre.trim()) return toast.error("Indica el nombre del cliente");
    try {
      if (editId) await api.put(`/proyectos/clientes/${editId}`, form);
      else await api.post("/proyectos/clientes", form);
      toast.success(editId ? "Cliente actualizado" : "Cliente creado");
      setForm(null); cargar(); onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const eliminar = async (cliente) => {
    if (!window.confirm(`¿Desactivar el cliente «${cliente.nombre}»?`)) return;
    try { await api.delete(`/proyectos/clientes/${cliente.id}`); toast.success("Cliente desactivado"); cargar(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return <div className="space-y-4">
    <div className="flex justify-end"><Button onClick={() => { setEditId(null); setForm(VACIO); }}><Plus className="mr-1 h-4 w-4" /> Nuevo cliente</Button></div>
    <div className="overflow-hidden rounded-xl border border-zinc-200">
      {items.length ? items.map((c) => <div key={c.id} className="flex items-center justify-between gap-3 border-b border-zinc-100 p-3 last:border-0">
        <div>
          <p className="font-medium text-zinc-900">
            {c.nombre}
            {c.codigo && <span className="text-xs text-zinc-500"> · {c.codigo}</span>}
            {c.es_potencial && (
              <span className="ml-2 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-violet-800">
                Potencial
              </span>
            )}
          </p>
          <p className="text-xs text-zinc-500">
            {[c.direccion, c.codigo_postal, c.poblacion, c.provincia, c.nif, c.telefono].filter(Boolean).join(" · ") || "Sin datos adicionales"}
            {c.propuesto_por ? ` · propuesto por ${c.propuesto_por}` : ""}
          </p>
        </div>
        <div className="flex gap-1"><Button variant="ghost" size="icon" onClick={() => { setEditId(c.id); setForm({ ...VACIO, ...c, geocodificar: true }); }}><Pencil className="h-4 w-4" /></Button><Button variant="ghost" size="icon" className="text-rose-600" onClick={() => eliminar(c)}><Trash2 className="h-4 w-4" /></Button></div>
      </div>) : <p className="p-8 text-center text-sm text-zinc-500">No hay clientes registrados.</p>}
    </div>
    <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}><DialogContent className="max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>{editId ? "Editar cliente" : "Nuevo cliente"}</DialogTitle></DialogHeader>
      {form && <div className="space-y-3">
        {[["nombre", "Nombre *"], ["codigo", "Código"], ["nif", "NIF"], ["email", "Email"], ["telefono", "Teléfono"], ["direccion", "Dirección"]].map(([key, label]) => (
          <div key={key}><Label>{label}</Label><Input value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} /></div>
        ))}
        <DireccionEspanaFields
          value={form}
          onChange={(next) => setForm({
            ...form,
            codigo_postal: next.codigo_postal || "",
            poblacion: next.poblacion || "",
            provincia: next.provincia || "",
          })}
        />
        <div><Label>Notas</Label><textarea className="min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.notas || ""} onChange={(e) => setForm({ ...form, notas: e.target.value })} /></div>
        {form.es_potencial && (
          <label className="flex items-center gap-2 rounded-lg border border-violet-100 bg-violet-50/60 px-3 py-2 text-sm text-violet-900">
            <input
              type="checkbox"
              checked={!form.es_potencial}
              onChange={(e) => setForm({ ...form, es_potencial: !e.target.checked })}
            />
            Cliente confirmado (dejar de tratar como potencial)
          </label>
        )}
      </div>}
      <DialogFooter><Button variant="outline" onClick={() => setForm(null)}>Cancelar</Button><Button onClick={guardar}>Guardar</Button></DialogFooter>
    </DialogContent></Dialog>
  </div>;
}
